import * as FramerMotion from 'framer-motion'
import * as R from 'ramda'
import * as React from 'react'
import * as ReactRouter from 'react-router-dom'
import * as Urql from 'urql'
import PropTypes from 'prop-types'

import * as Common from '@rushplay/common'
import * as I18n from '@rushplay/i18n'

import * as Icons from './icons'
import { Button } from './button'
import { CasinoGameTile } from './casino-game-tile'
import { Divider } from './divider'
import { GameTileAnimation } from './game-tile-animation'
import { GameTileSection } from './game-tile-section'
import { HtmlContent } from './html-content'
import { Parallax } from './parallax'
import { mapIndexed } from './map-indexed'
import { usePaginatedQuery } from './use-paginated-query'

function CasinoSection(props) {
  const location = ReactRouter.useLocation()

  return (
    <GameTileSection
      id={props.sectionKey}
      columns={R.length(props.games)}
      layout={props.layout}
      sectionTitle={`${props.categoryKey}.${props.sectionKey}`}
      size={props.tileSize}
    >
      {data => (
        <FramerMotion.AnimatePresence>
          {mapIndexed(
            (game, index) => (
              <GameTileAnimation
                key={`${props.categoryKey}-${game.id}`}
                delay={(index % 24) * 0.04}
              >
                <ReactRouter.Link
                  to={`/casino/games/${game.id}?referrer=${R.drop(
                    1,
                    location.pathname
                  )}&mode=classic`}
                >
                  <Parallax>
                    <CasinoGameTile
                      id={parseInt(game.id)}
                      backgroundOverlayUrl={game.backgroundOverlayUrl}
                      backgroundOverlayImageAlignment={
                        game.backgroundOverlayImageAlignment
                      }
                      backgroundUrl={game.backgroundUrl}
                      jackpotValue={game.jackpotValue}
                      jackpotCurrency={game.jackpotCurrency}
                      title={game.title}
                      titleUrl={game.titleUrl}
                      provider={game.displayProvider || game.provider}
                      gameKey={game.key}
                      size={props.tileSize}
                    />
                  </Parallax>
                </ReactRouter.Link>
              </GameTileAnimation>
            ),
            R.take(data.visibleGames, props.games)
          )}
        </FramerMotion.AnimatePresence>
      )}
    </GameTileSection>
  )
}

CasinoSection.propTypes = {
  layout: PropTypes.string,
  sectionKey: PropTypes.string,
  categoryKey: PropTypes.string,
  games: PropTypes.array,
  tileSize: PropTypes.string,
}

const getCasinoSection = `
  query CasinoLoadMoreSection($id: ID!, $limit: Int!, $offset: Int!) {
    catalogSection(id: $id, limit: $limit, offset: $offset) {
      games {
        backgroundUrl
        backgroundOverlayImageAlignment
        backgroundOverlayUrl
        displayProvider
        id
        jackpotCurrency
        jackpotValue
        key
        provider
        title
        titleUrl
      }
    }
  }
`

function loadMoreGamesResolver(prevData, newData) {
  const normalizedGames = R.flatten(
    R.prepend(
      R.pathOr([], ['catalogSection', 'games'], prevData),
      R.pathOr([], ['catalogSection', 'games'], newData)
    )
  )

  return R.assocPath(['catalogSection', 'games'], normalizedGames, newData)
}

function CasinoGridSection(props) {
  const [response, fetchMore] = usePaginatedQuery({
    query: getCasinoSection,
    variables: { id: props.id, limit: 24, offset: R.length(props.games) },
    resolver: loadMoreGamesResolver,
  })
  const i18n = I18n.useI18n()
  const sectionGames = R.pathOr([], ['catalogSection', 'games'], response.data)
  const games = R.flatten(R.prepend(props.games, sectionGames))

  if (response.error && R.isEmpty(games)) {
    return (
      <Common.Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        fontSize={6}
      >
        <Icons.Error />
        <Common.Box fontSize={2}>
          {i18n.translate('errors.casino-section.loading.failed')}
        </Common.Box>
      </Common.Box>
    )
  }

  if (response.fetching && R.isEmpty(games)) {
    return (
      <Common.Box
        display="flex"
        color="spinner-color"
        justifyContent="center"
        alignSelf="center"
        fontSize={['48px', '64px']}
        pt={3}
      >
        <Icons.Spinner delay />
      </Common.Box>
    )
  }

  if (R.isEmpty(games)) {
    return null
  }

  return (
    <Common.Box zIndex="99" position="relative" pb="3">
      <CasinoSection
        games={games}
        id={props.id}
        sectionKey={props.sectionKey}
        categoryKey={props.categoryKey}
        tileSize={props.tileSize}
        layout="grid"
      />
      {props.totalGamesCount > R.length(games) && (
        <Common.Box display="flex" justifyContent="center" pt="4">
          <Button disabled={response.fetching} onClick={fetchMore}>
            {i18n.translate('casino-category.show-more')}
          </Button>
        </Common.Box>
      )}
    </Common.Box>
  )
}

CasinoGridSection.defaultProps = {
  games: [],
}

CasinoGridSection.propTypes = {
  id: PropTypes.string,
  categoryKey: PropTypes.string,
  games: PropTypes.array,
  sectionKey: PropTypes.string,
  tileSize: PropTypes.string,
  totalGamesCount: PropTypes.number,
}

const getCasinoCategory = `
  query CasinoCategory($id: ID!) {
    catalogCategory(id: $id) {
      id
      sections {
        id
        key
        layout
        tileSize
        totalGamesCount
        games {
          backgroundUrl
          backgroundOverlayImageAlignment
          backgroundOverlayUrl
          displayProvider
          id
          jackpotCurrency
          jackpotValue
          key
          provider
          title
          titleUrl
        }
      }
    }
  }
`

export function CasinoCategory() {
  const params = ReactRouter.useParams()
  const [response] = Urql.useQuery({
    query: getCasinoCategory,
    variables: { id: params.id },
  })
  const i18n = I18n.useI18n()

  const categoryKey = params.slug

  if (response.error) {
    return (
      <Common.Box
        alignItems="center"
        color="g-text"
        display="flex"
        flexDirection="column"
        flexGrow="1"
        fontSize="48px"
        justifyContent="center"
        pt={3}
      >
        <Icons.Error />
        <Common.Box fontSize={2} pt={2}>
          {i18n.translate('errors.casino-category.loading.failed')}
        </Common.Box>
      </Common.Box>
    )
  }

  if (response.fetching) {
    return (
      <Common.Box
        alignSelf="center"
        color="spinner-color"
        display="flex"
        flexGrow="1"
        fontSize={['48px', '64px']}
        justifyContent="center"
        pt={3}
      >
        <Icons.Spinner delay />
      </Common.Box>
    )
  }

  return (
    <React.Fragment>
      {R.map(
        section =>
          section.layout === 'grid' ? (
            <CasinoGridSection
              key={`${params.id}-${section.key}`}
              id={section.id}
              sectionKey={section.key}
              categoryKey={categoryKey}
              tileSize={section.tileSize}
              totalGamesCount={section.totalGamesCount}
              games={section.games}
            />
          ) : (
            <Common.Box
              zIndex="9"
              position="relative"
              pb="3"
              key={`${params.id}-${section.key}`}
            >
              <CasinoSection
                games={section.games}
                id={section.id}
                sectionKey={section.key}
                categoryKey={categoryKey}
                tileSize={section.tileSize}
                layout={section.layout}
              />
            </Common.Box>
          ),
        R.pathOr([], ['catalogCategory', 'sections'], response.data)
      )}
      {i18n.hasTranslation(`casino-category.${categoryKey}.seo-content`) && (
        <Common.Box p={2}>
          <Divider />
          <Common.Box
            pt={2}
            maxWidth="1080px"
            margin="0 auto"
            textAlign="left"
            fontSize={2}
          >
            <HtmlContent
              html={{
                __html: i18n.translate(
                  `casino-category.${categoryKey}.seo-content`
                ),
              }}
            />
          </Common.Box>
        </Common.Box>
      )}
    </React.Fragment>
  )
}

// For @loadable/components
export default CasinoCategory
